'use client'
import React, { useEffect, useState } from 'react'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import { ICategory } from '@/app/[locale]/_types'
import {
  AspectRatio,
  Box,
  Center,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import Image from 'next/image'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import ClassNames from 'embla-carousel-class-names'
import { Button } from '@/components/ui/button'
import { Avatar } from '@/components/ui/avatar'
import { Tooltip } from '@/components/ui/tooltip'
import { IExtendedCategory } from '../CategoryCard'
import { useRouter } from '@/i18n/navigation'
import '@/app/[locale]/_styles/embla.css'

interface CategoryCarouselProps {
  categories: (ICategory | IExtendedCategory)[]
}

const NextButton = ({ onClick, disabled }: any) => (
  <Button
    className='embla__button embla__button--next'
    bg={'#FFFFFF26'}
    mr={-12}
    color={'light'}
    rounded={'full'}
    onClick={onClick}
    disabled={disabled}
    aria-label='Next'
  >
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M9 18l6-6-6-6'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </Button>
)

const PrevButton = ({ onClick, disabled }: any) => (
  <Button
    className='embla__button embla__button--prev'
    bg={'#FFFFFF26'}
    ml={-12}
    color={'light'}
    rounded={'full'}
    onClick={onClick}
    disabled={disabled}
    aria-label='Previous'
  >
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M15 18l-6-6 6-6'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </Button>
)

const usePrevNextButtons = (emblaApi: any) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onPrevButtonClick = React.useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = React.useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  }
}

const CategoryCarousel = ({ categories }: CategoryCarouselProps) => {
  const currentLocale = useCustomLocale()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const router = useRouter()

  // Adjust slide width based on number of categories
  const slideWidth = useBreakpointValue({
    base:
      categories.length === 1
        ? '100%'
        : categories.length === 2
        ? 'calc(50% - 10px)'
        : categories.length === 3
        ? 'calc(33.3333% - 13.3333px)'
        : 'calc((100% - 2 * 16px) / 3)',
    lg:
      categories.length === 1
        ? '100%'
        : categories.length === 2
        ? 'calc(50% - 10px)'
        : categories.length === 3
        ? 'calc(33.3333% - 13.3333px)'
        : 'calc(100% / 3 - 18px)',
    xl:
      categories.length === 1
        ? '100%'
        : categories.length === 2
        ? 'calc(50% - 10px)'
        : categories.length === 3
        ? 'calc(33.3333% - 13.3333px)'
        : 'calc(25% - 18px)',
  })

  const maxWidth = useBreakpointValue({
    base: '92vw',
    '2xl': '1288px',
  })

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: false,
      align: 'start',
      dragFree: categories.length >= 4, // Disable dragFree for < 4 categories
      containScroll: 'trimSnaps', // Ensure proper snap behavior
    },
    categories.length >= 4 ? [ClassNames(), Autoplay()] : [ClassNames()] // Disable Autoplay for < 4 categories
  )

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi)

  useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap())
    }

    emblaApi.on('select', onSelect)
    onSelect()

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi])

  return (
    <>
      <Box
        as='section'
        className='embla'
        position='relative'
        maxW={{ base: '99vw', lg: '1376px' }}
        mb={10}
      >
        <Box
          maxW={maxWidth}
          className='embla__viewport'
          ref={emblaRef}
          overflow='hidden'
        >
          <Box
            className='embla__container'
            display={'flex'}
            gap={{ base: 4, lg: 6 }}
          >
            {categories?.map((item, index) => (
              <Box
                key={index}
                className='embla__slide'
                style={{
                  boxSizing: 'border-box',
                }}
                minW={slideWidth}
                flex={{
                  base:
                    categories.length === 1
                      ? '0 0 100%'
                      : categories.length === 2
                      ? '0 0 calc(50% - 10px)'
                      : categories.length === 3
                      ? '0 0 calc(33.3333% - 13.3333px)'
                      : '0 0 calc(33.3333% - 18px)',
                  lg:
                    categories.length === 1
                      ? '0 0 100%'
                      : categories.length === 2
                      ? '0 0 calc(50% - 10px)'
                      : categories.length === 3
                      ? '0 0 calc(33.3333% - 13.3333px)'
                      : '0 0 calc(25% - 18px)',
                }}
                onClick={() => router.push('/landing-menu#' + item.slug)}
                cursor={'pointer'}
              >
                <Center
                  flexDir='column'
                  gap={3}
                  height='100%'
                  p={{ base: 0, lg: 2 }}
                >
                  <AspectRatio
                    ratio={1 / 1}
                    minW={{ base: '80px', lg: '288px' }}
                    w='full'
                    rounded={{ base: '16px', lg: '24px' }}
                    overflow='hidden'
                  >
                    {item?.image ? (
                      <Image
                        src={process.env.BASE_URL + item?.image}
                        alt={item?.title?.[currentLocale] || 'Category'}
                        style={{ objectFit: 'cover' }}
                        fill
                      />
                    ) : (
                      <Avatar
                        name={item?.title?.[currentLocale] || 'Brand'}
                        w={'48px'}
                        height={'48px'}
                        shape={'square'}
                        size={'lg'}
                        opacity={0.2}
                      />
                    )}
                  </AspectRatio>
                  <Tooltip content={item?.title?.[currentLocale]}>
                    <Text
                      fontWeight={600}
                      truncate
                      maxW='full'
                      fontSize={{ base: 14, lg: 22 }}
                    >
                      {item?.title?.[currentLocale]}
                    </Text>
                  </Tooltip>
                </Center>
              </Box>
            ))}
          </Box>
        </Box>

        {categories.length >= 4 && (
          <Box display={{ base: 'none', lg: 'flex' }}>
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </Box>
        )}
      </Box>
    </>
  )
}

export default CategoryCarousel
